"use client";

import React, { FC, RefObject, useCallback, useEffect, useState } from "react";
import SwiperCore from "swiper";
import { SwiperOptions } from "swiper/types";

import useBreakPoint from "@/hooks/useBreakpoint";

import { HistorySlideCard } from "./HistoryCard";
import { History, HistoryCard } from "./types";

interface Props {
  data: History;
  carouselRef: RefObject<HTMLDivElement>;
  containerRef: RefObject<HTMLDivElement>;
  orderedFirstSlideIndex: {
    title: string;
    year?: number;
    firstIndex: number;
    bulletIcon?: any;
  }[];
  currentBulletIndex?: number;
  swiperInstance?: SwiperCore;
  setCurrentYearGroupIndex: Function;
  setOpenModalCard: Function;
  setCurrentBulletIndex: Function;
  setCurrentCard: Function;
  setSwiperInstance: Function;
  cardsList: {
    yearGroupIndex: number;
    card: HistoryCard;
  }[];
}
const HistoryMobile: FC<Props> = ({
  data,
  carouselRef,
  containerRef,
  orderedFirstSlideIndex,
  currentBulletIndex,
  cardsList,
  swiperInstance,
  setSwiperInstance,
  setCurrentYearGroupIndex,
  setCurrentBulletIndex,
  setOpenModalCard,
  setCurrentCard,
}) => {
  const { isMd } = useBreakPoint("md");

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const container = containerRef.current;
  const minSwipeDistance = 50;

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: TouchEvent) => {
      setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
      if (!touchStart || !touchEnd) return;

      const distance = touchStart - touchEnd;
      const isSwipe = Math.abs(distance) > minSwipeDistance;

      if (!isSwipe || !swiperInstance) return;

      if (distance > 0) {
        swiperInstance.slideNext();
      } else {
        swiperInstance.slidePrev();
      }
    };

    if (container && !isMd) {
      container.addEventListener("touchstart", handleTouchStart);
      container.addEventListener("touchmove", handleTouchMove);
      container.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      if (container && !isMd) {
        container.removeEventListener("touchstart", handleTouchStart);
        container.removeEventListener("touchmove", handleTouchMove);
        container.removeEventListener("touchend", handleTouchEnd);
      }
    };
  }, [touchStart, touchEnd, swiperInstance, isMd, containerRef]);

  const carousel = useCallback(() => {
    if (carouselRef.current && isMd === false) {
      swiperInstance?.destroy();
      const props: SwiperOptions = {
        direction: "horizontal",
        slidesPerView: "auto",
        centeredSlides: false,
        spaceBetween: 24,
        speed: 800,
        slidesOffsetBefore: 16,
        slidesOffsetAfter: 16,
        mousewheel: {
          enabled: true,
          releaseOnEdges: true,
        },
        pagination: {
          el: ".pagination-bottom",
          type: "progressbar",
          clickable: true,
        },
        touchRatio: 1,
        on: {
          click(swiper) {
            swiper.slideTo(swiper.clickedIndex);
          },
          slideChange(swiper) {
            setCurrentBulletIndex(undefined);
            const yearGroupIndex = swiper.slides[swiper.activeIndex].getAttribute("data-year-index");
            if (yearGroupIndex) {
              setCurrentYearGroupIndex(+yearGroupIndex);
              setCurrentCard(cardsList[swiper.activeIndex].card);
            }
          },
        },
      };
      const swiper = new SwiperCore(carouselRef.current, props);
      setSwiperInstance(swiper);
    }
  }, [isMd]);

  useEffect(() => {
    carousel();
  }, [isMd]);

  useEffect(() => {
    if (swiperInstance && currentBulletIndex !== undefined) {
      swiperInstance?.slideTo(orderedFirstSlideIndex[currentBulletIndex].firstIndex);
    }
  }, [currentBulletIndex]);

  return isMd === false ? (
    <>
      <div className="swiper relative w-full order-2 md:order-1 history-card-carousel snap-center" ref={carouselRef}>
        <div className="swiper-wrapper items-end">
          {data.years.map((item, index) => {
            return item.cards.map((card, cardIndex) => {
              return (
                <div key={index + "-" + cardIndex} className="relative swiper-slide" data-year-index={index}>
                  <span className="observer-target absolute top-2/4 h-4xl w-full hidden md:block z-10 -translate-y-2/4 pointer-events-none"></span>
                  <HistorySlideCard card={card} index={index} year={item.year} setOpenModalCard={setOpenModalCard} />
                </div>
              );
            });
          })}
        </div>
      </div>
    </>
  ) : (
    <></>
  );
};
export default HistoryMobile;
